import { Mutex } from 'async-mutex';
import { useCallback } from 'react';

import { logger } from '../../datadog';
import { useUpdateCachedReflectionTopicCompletionStatusCallback } from '../../hooks/reflections/useUpdateCachedReflectionTopicCompletionStatusCallback';
import { useHandleWhiteboardUpdateCallback } from '../../session/messages/useHandleWhiteboardUpdateMessage';
import { DataChannelMessage, DataChannelMessageType } from '../../video-sdk';
import { useHandleReceivedSessionIntentionEditedCallback } from './useHandleReceivedSessionIntentionEditedCallback';
import { useHandleSessionIntentionPromptAcknowledgedCallback } from './useHandleSessionIntentionPromptAcknowledgedCallback';
import { useUpdateDataChannelAppStateCallback } from './useUpdateDataChannelAppStateCallback';

const handleReceivedMessageMutex = new Mutex();

/**
 * Returns a callback that handles received messages on the data track for the
 * provider
 */
export function useHandleReceivedMessageCallback() {
  const updateDataTrackState = useUpdateDataChannelAppStateCallback();
  const updateWhiteboard = useHandleWhiteboardUpdateCallback();
  const updateCachedTopicCompletionStatus = useUpdateCachedReflectionTopicCompletionStatusCallback();
  const handleReceivedSessionIntentionEdited = useHandleReceivedSessionIntentionEditedCallback();
  const handleSessionIntentionPromptAcknowledged = useHandleSessionIntentionPromptAcknowledgedCallback();

  return useCallback(
    (message: DataChannelMessage) => {
      return handleReceivedMessageMutex.runExclusive(async () => {
        logger.info('Received data channel message', { messageType: message.type });

        switch (message.type) {
          // Handles messages related to app state: consent, recording, whiteboard mode, etc.
          case DataChannelMessageType.APP_STATE:
            await updateDataTrackState(message);
            break;
          // Handles incoming whiteboard updates
          case DataChannelMessageType.WHITEBOARD_UPDATE:
            await updateWhiteboard(message);
            break;
          case DataChannelMessageType.REFLECTION_TOPIC_UPDATE:
            updateCachedTopicCompletionStatus(message.topic);
            break;
          case DataChannelMessageType.SESSION_INTENTION_EDITED:
            await handleReceivedSessionIntentionEdited(message);
            break;
          case DataChannelMessageType.SESSION_INTENTION_PROMPT_ACKNOWLEDGED:
            await handleSessionIntentionPromptAcknowledged(message);
            break;
        }
      });
    },
    [
      updateCachedTopicCompletionStatus,
      updateDataTrackState,
      updateWhiteboard,
      handleReceivedSessionIntentionEdited,
      handleSessionIntentionPromptAcknowledged,
    ],
  );
}
